/**
 * Right-side detail panel for a Meta campaign in the unified ``/campaigns`` surface (#4173 Chunk 2).
 *
 * Mirrors the structure of the Google detail panel at
 * ``routes/_authenticated/_layout/campaigns/google/$campaignslug/_slug/index.tsx``: a row of tab buttons
 * sitting above a ``<Card>`` whose contents change per tab. Tab list is intentionally smaller than
 * Google's (Overview / Assets) for MVP — Google's Budget / Conversions / Negatives don't have direct
 * Meta analogs and can be added later if Dave wants them.
 *
 * Lifted from the inline ``MetaCampaignDetail`` accordion that used to live in
 * ``routes/_authenticated/_layout/meta-campaigns/index.tsx`` (now a sunset redirect to the unified
 * surface — see #4173 Chunk 6); kept here as a standalone component so the unified campaigns surface
 * can slot it in next to ``CampaignSidebar``.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertCircle, ArrowRight, ImageOff, Loader2, Plus, Sparkles } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Spinner } from '@/components/glitch-ui/spinner';
import { DataTable } from '@/components/glitch-ui/data-table';
import { EditableField } from '@/components/glitch-ui/editable-field';
import { tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { MetaCampaignActionsDropdown } from '@/components/campaign/meta/meta-actions-dropdown';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { useSetAtom } from 'jotai';

import { metaAudienceDraftAtom, metaBudgetDraftAtom, metaInterestsDraftAtom, metaLocationsDraftAtom, metaPlacementsDraftAtom, selectedMetaCampaignTabAtom } from '@/store';
import { useToast } from '@/hooks/useToast';
import { MetaCampaignActivityFeed } from '@/components/campaign/CampaignActivityFeed';
import { AddMetaAdSetModal } from '@/components/campaign/meta/add-meta-ad-set-modal';
import { MetaBudgetTab, MetaConversionsTab, MetaTargetingTab } from '@/components/campaign/meta/meta-campaign-tabs';
import { CampaignInfoForm } from '@/components/campaign/detail/info-form';
import {
  metaCampaignInfoFields,
  useMetaAudienceDraftSave,
  useMetaBudgetDraftSave,
  useMetaInterestsDraftSave,
  useMetaLaunchControl,
  useMetaLocationsDraftSave,
  useMetaPauseControl,
  useMetaPlacementsDraftSave,
} from '@/components/campaign/meta/meta-detail-adapter';
import {
  metaCampaignDetailQueryOptions,
  metaAdSetPerformanceQueryOptions,
  metaCampaignProgressQueryOptions,
  metaCampaignTaskStatusQueryOptions,
  metaIntegrationStatusQueryOptions,
  creativeAssetQueryOptions,
  useDeleteMetaCampaignMutation,
  useRegenerateMetaAdCopyMutation,
  useUpdateMetaAdCopyMutation,
  useUpdateMetaCampaignTargetingMutation,
} from '@/lib/query-options';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { usePlatformConnections } from '@/hooks/usePlatformConnections';
import { PlatformConnectionBanner } from '@/components/campaign/platform-connection-banner';
import { formatCents, formatCtr, formatFrequency, humanizeMetaEnum } from '@/components/campaign/meta/meta-metrics';
import type {
  MetaAdCopyUpdateRequest,
  MetaAdResponse,
  MetaAdSetPerformanceRow,
  MetaAdSetResponse,
  MetaCampaignDetailResponse,
  MetaCampaignGenerationProgress,
  MetaCreativePerformanceRow,
} from '@/lib/types';

type MetaCampaignTabId = 'overview' | 'assets' | 'targeting' | 'budget' | 'conversions';

// Google-parity tab set with Meta-native semantics (#5981): Targeting/Budget/Conversions exist
// because Meta has real concepts behind them. Negatives and the search-ad extension sub-tabs are
// deliberately absent — no Meta analog.
const TABS: { id: MetaCampaignTabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'assets', label: 'Assets' },
  { id: 'targeting', label: 'Targeting' },
  { id: 'budget', label: 'Budget' },
  { id: 'conversions', label: 'Conversions' },
];

/**
 * Top-of-page section for the Meta detail surface (#4288 follow-up): generating progress card,
 * failure banner, and approval-flow header. Lifted out of ``MetaCampaignDetailPanel`` so the
 * route can render it **above** the campaign-list grid at full width — mirroring how Google's
 * route puts ``<CampaignApprovalFlow>`` above the 12-col grid. Pre-fix, this content lived inside
 * the 9-col panel cell and felt cramped next to the sidebar.
 *
 * Both components query the same ``metaCampaignDetailQueryOptions(slug)``; TanStack Query
 * dedupes the network call, so there's no double-fetch.
 */
export function MetaCampaignDetailHeader({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useQuery(metaCampaignDetailQueryOptions(slug));
  // Meta not connected (#5937): the campaign stays viewable but the mutating surfaces
  // (launch/pause in the panel toolbar) are suppressed — the Meta sibling of the Google detail
  // page folding isGoogleDisconnected into isReadOnly. Loading counts as connected so the
  // header doesn't flash the banner while the status resolves.
  const { metaConnected, isLoading: isLoadingConnections } = usePlatformConnections();
  const isMetaDisconnected = !isLoadingConnections && !metaConnected;
  // Polling is keyed by offer_url because Meta's task tracking is offer-scoped — every campaign in
  // the same three-set shares the offer_url and the same task. The query polls every 3s while state
  // is IN_PROGRESS and stops automatically when COMPLETED/FAILED.
  const offerUrl = data?.offer_url ?? null;
  const taskStatus = useQuery(metaCampaignTaskStatusQueryOptions(offerUrl));
  const progress = useQuery(metaCampaignProgressQueryOptions(slug));
  const isStuck = taskStatus.data?.is_stuck === true;
  const hasFailed = taskStatus.data?.state === 'FAILED';
  const failureMessage = taskStatus.data?.error_message;

  // Refetch the campaign detail whenever the generation progress moves — on every tick (ad-set/ad
  // counts incrementing) AND on the terminal transition. Per-tick means freshly-created ads stream
  // into the Assets tab as the task fans out, and a missed terminal transition can't strand the tab
  // on stale zero-ads data (pre-fix, the tab needed a manual browser refresh). Tracked off the
  // per-campaign progress endpoint because its state is read from the same TaskTracking row as
  // ``taskStatus`` but is keyed by campaign-id, which avoids cross-campaign invalidation races
  // inside a three-set.
  const progressTick =
    progress.data == null
      ? null
      : `${progress.data.state}:${progress.data.ad_sets.completed}:${progress.data.ads.completed}`;
  // A campaign already terminal (COMPLETED/FAILED) when the panel first opens isn't generating — its
  // ads are already loaded — so skip the invalidate/refetch that otherwise fired on every visit
  // (#6163 P1-13). Active generations (non-terminal first state) still stream ads in per tick.
  // The latch un-sets on IN_PROGRESS (#6186): after a RETRY, the remounted panel first observes the
  // failed attempt's cached terminal state, and a permanent latch would then suppress every
  // invalidation for the new run — Go Live and the sidebar sat on stale zero-ad-set data until a
  // manual refresh.
  const firstProgressStateRef = useRef<string | null>(null);
  useEffect(() => {
    if (progressTick == null) return;
    const state = progressTick.split(':')[0];
    if (firstProgressStateRef.current == null || state === 'IN_PROGRESS') firstProgressStateRef.current = state;
    const first = firstProgressStateRef.current;
    if (first === 'COMPLETED' || first === 'FAILED') return;
    void queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', slug] });
    // Also refresh the sidebar's campaigns list so its status pill flips out of "generating"
    // when the task completes (the list is a separate cache entry from the detail).
    void queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] });
  }, [progressTick, queryClient, slug]);

  const handleRetry = () => {
    if (!offerUrl) return;
    // Wizard reads ``?offer_url=…`` and pre-fills the field; user only re-enters page id + daily
    // budget. ``phase`` is required post-#4277 chunk 2c.
    navigate({
      to: '/new/campaign/meta',
      search: { offer_url: offerUrl, phase: data?.campaign_type ?? 'TESTING' },
    });
  };

  return (
    <>
      {/* Generation progress is NOT rendered here — it lives in the content panel
          (MetaCampaignDetailPanel) so it sits in the col-span-9 column like Google's. Post-#5981
          redesign this header carries only the failed/stuck strip (Google's CampaignFailedBanner
          visual) and the not-connected banner; launching/pausing moved into the panel's compact
          toolbar, replacing the full-width approval flow. */}
      {(hasFailed || isStuck) && (
        <div className="bg-[#FEE2E2] border-l-4 border-l-[#FCA5A5] rounded-lg p-6 mb-4">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-lg shadow p-3 self-start mt-0.5">
              <AlertCircle className="h-6 w-6 text-[#B91C1C]" />
            </div>
            <div className="flex flex-1 items-center justify-between gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isStuck ? 'Campaign Stuck Building' : 'Campaign Creation Failed'}
                </h3>
                <p className="text-gray-900 whitespace-pre-line">
                  {isStuck
                    ? 'This campaign has been building for longer than expected. You can restart it to try again.'
                    : failureMessage || 'The Meta campaign-creation task failed. Open the logs for details.'}
                </p>
              </div>
              <div className="shrink-0">
                {/* Restart goes back through the wizard (offer URL pre-filled); the router reuses
                    this campaign's draft row, so it restarts THIS campaign — same slug (#5981). */}
                <Button
                  size="lg"
                  className="bg-[#290BD9] hover:bg-[#2309C5]"
                  onClick={handleRetry}
                  disabled={!offerUrl}
                >
                  RETRY
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meta not connected (#5937): connect banner replaces the mutating surfaces —
          launching or pausing would only fail without a connection. The campaign's
          tabs stay fully viewable in the content panel. */}
      {isMetaDisconnected && <PlatformConnectionBanner platform="meta" />}
    </>
  );
}

export function MetaCampaignDetailPanel({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  // The hash carries the main tab, optionally with a nested sub-tab: `#assets/ad-sets` (#6169). Split
  // it so the main-tab persistence only ever sees the first segment, while the sub-tab stays linkable.
  const [mainSegment, subSegment] = (location.hash ?? '').replace('#', '').split('/');
  // Google's tab-persistence pattern (#5981): URL hash first, Jotai atom as the fallback when the
  // hash is absent (e.g. re-entering from the sidebar), so switching to another campaign and back
  // restores the tab you were on.
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedMetaCampaignTabAtom,
    TABS.map((tab) => tab.id),
    'overview',
    (tabId, replace) =>
      navigate({ to: '/campaigns/meta/$metaCampaignSlug', params: { metaCampaignSlug: slug }, hash: tabId, replace }),
    mainSegment ? `#${mainSegment}` : location.hash,
  );
  // Sub-tab navigation for the Assets tab: keep the `assets` main segment, swap the sub segment so the
  // URL is shareable and survives refresh (#6169). `replace` on the default so it adds no history entry.
  const navigateToAssetsSubTab = useCallback(
    (subId: string, replace = false) =>
      navigate({
        to: '/campaigns/meta/$metaCampaignSlug',
        params: { metaCampaignSlug: slug },
        hash: `assets/${subId}`,
        replace,
      }),
    [navigate, slug],
  );
  const { data, isLoading, isError } = useQuery(metaCampaignDetailQueryOptions(slug));
  const { toast } = useToast();
  // Meta ad accounts can be in any currency — read the active selection's currency for the symbol.
  const { data: metaStatus } = useQuery(metaIntegrationStatusQueryOptions());
  const { symbol: currencySymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const { metaConnected, isLoading: isLoadingConnections } = usePlatformConnections();
  const isMetaDisconnected = !isLoadingConnections && !metaConnected;

  // Generation progress renders *here*, inside the content panel (col-span-9), to mirror Google's
  // detail page where the "Generating Campaign" banner lives in the content column — not full-width
  // across the top like the approval flow. Queries dedupe with the header's via React Query's cache.
  const offerUrl = data?.offer_url ?? null;
  const taskStatus = useQuery(metaCampaignTaskStatusQueryOptions(offerUrl));
  const progress = useQuery(metaCampaignProgressQueryOptions(slug));
  // Both signals gate generation: taskStatus is OFFER-keyed and progress is CAMPAIGN-keyed, and
  // during a retry their caches can briefly disagree — the toolbar rendered Go Live + the unmet
  // checklist over a still-generating campaign (#6215 test round). Either saying IN_PROGRESS
  // means generating.
  const isGenerating = taskStatus.data?.state === 'IN_PROGRESS' || progress.data?.state === 'IN_PROGRESS';

  // Launch / pause live in the tab row's right corner post-#5981 — Google's compact placement,
  // replacing the full-width three-step approval strip. The readiness checklist collapses into
  // the disabled Go Live button's tooltip.
  const launchControl = useMetaLaunchControl(data, isGenerating);
  const { isPausing, pause } = useMetaPauseControl(data);
  const showLaunchActions = !!data && !isGenerating && !isMetaDisconnected && taskStatus.data?.state !== 'FAILED';
  // Delete stays available on FAILED campaigns (Google parity: a failed campaign's only exit is
  // deletion) but not mid-generation (the create task is pushing to Meta — archiving under it would
  // race) and not while disconnected (the connect banner replaces mutating surfaces, #5937).
  const canDelete = !!data && !isGenerating && !isMetaDisconnected;
  // The Budget tab's staged edits commit from HERE — Google's toolbar Save Changes placement
  // (#5981). The draft atom is campaign-keyed; clear it when this panel switches campaigns so a
  // stale draft never leaks across.
  const draftSave = useMetaBudgetDraftSave(data);
  // Interests and placements each stage into their own campaign-keyed draft; the one Save Changes
  // below commits every dirty draft together, shown in place of Go Live until saved (#6076). Clear
  // all drafts when the panel switches campaigns so a stale draft never leaks across.
  const interestsDraftSave = useMetaInterestsDraftSave(data);
  const placementsDraftSave = useMetaPlacementsDraftSave(data);
  const audienceDraftSave = useMetaAudienceDraftSave(data);
  const locationsDraftSave = useMetaLocationsDraftSave(data);
  const setBudgetDraft = useSetAtom(metaBudgetDraftAtom);
  const setInterestsDraft = useSetAtom(metaInterestsDraftAtom);
  const setPlacementsDraft = useSetAtom(metaPlacementsDraftAtom);
  const setAudienceDraft = useSetAtom(metaAudienceDraftAtom);
  const setLocationsDraft = useSetAtom(metaLocationsDraftAtom);
  useEffect(() => {
    setBudgetDraft(null);
    setInterestsDraft(null);
    setPlacementsDraft(null);
    setAudienceDraft(null);
    setLocationsDraft(null);
  }, [slug, setBudgetDraft, setInterestsDraft, setPlacementsDraft, setAudienceDraft, setLocationsDraft]);

  // Soft delete (#6179): Google's Delete Campaign analog — confirm modal, archive, back to the list.
  const deleteCampaign = useDeleteMetaCampaignMutation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleConfirmDelete = async () => {
    if (!data) return;
    setShowDeleteConfirm(false);
    try {
      await deleteCampaign.mutateAsync(data.slug);
      // Clear staged drafts BEFORE navigating so the unsaved-changes blocker can't intercept the
      // exit of a campaign that no longer exists.
      setBudgetDraft(null);
      setInterestsDraft(null);
      setPlacementsDraft(null);
      setAudienceDraft(null);
      setLocationsDraft(null);
      navigate({ to: '/campaigns', replace: true });
    } catch (error) {
      toast({
        title: 'Could Not Delete Campaign',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Save Changes and Go Live are mutually exclusive: any staged budget/interest/placement/audience/
  // location edit hides Go Live and shows a single Save Changes that commits them all.
  const updateTargeting = useUpdateMetaCampaignTargetingMutation();
  const hasUnsavedChanges =
    draftSave.isDirty ||
    interestsDraftSave.isDirty ||
    placementsDraftSave.isDirty ||
    audienceDraftSave.isDirty ||
    locationsDraftSave.isDirty;
  const anySaving = draftSave.isSaving || updateTargeting.isPending;
  // Resolves true only when EVERYTHING staged persisted — the unsaved-changes modal gates
  // navigation on it, so a failed save keeps the user on the page instead of losing the drafts.
  const saveAllChanges = async (): Promise<boolean> => {
    if (draftSave.isDirty && !(await draftSave.save())) return false;
    // Every staged targeting slice commits in ONE combined PATCH (#6179) — the endpoint applies
    // each provided field in a single write; per-slice saves fired up to three sequential PATCHes.
    // All-or-nothing: a validation error on any dirty slice blocks the whole targeting save.
    if (!data) return false;
    const dirtySlices = [interestsDraftSave, placementsDraftSave, audienceDraftSave, locationsDraftSave].filter(
      (s) => s.isDirty,
    );
    if (dirtySlices.length === 0) return true;
    const invalid = dirtySlices.find((s) => s.validationError != null);
    if (invalid) {
      toast({ title: 'Could Not Save Changes', description: invalid.validationError ?? '', variant: 'destructive' });
      return false;
    }
    const body = Object.assign({}, ...dirtySlices.map((s) => s.bodyFragment));
    try {
      await updateTargeting.mutateAsync({ slug: data.slug, body });
      dirtySlices.forEach((s) => s.clearDraft());
      return true;
    } catch (error) {
      toast({
        title: 'Could Not Save Changes',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
      return false;
    }
  };
  // Block leaving the campaign (to another campaign or elsewhere) with staged edits, using the shared
  // unsaved-changes modal. Moving within the campaign — between ads, or an ad back to the parent — is
  // allowed via allowedBasePath (#6076).
  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty: hasUnsavedChanges,
    // The modal navigates once onSave resolves, so a save that didn't fully persist (validation
    // or server failure, both toasted inside saveAllChanges) must throw to keep the user here.
    onSave: async () => {
      if (!(await saveAllChanges())) {
        throw new Error('The changes were not saved. Fix the reported issue or discard them.');
      }
    },
    onDiscard: () => {
      setBudgetDraft(null);
      setInterestsDraft(null);
      setPlacementsDraft(null);
      setAudienceDraft(null);
      setLocationsDraft(null);
    },
    isSaving: anySaving,
    allowedBasePath: `/campaigns/meta/${slug}`,
    contextName: data?.name,
  });

  return (
    <div>
      <div className="flex items-end justify-between">
        <div className="flex items-end gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`${tabButtonClass(activeTab === tab.id)} cursor-pointer`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {(showLaunchActions || hasUnsavedChanges || canDelete) && (
          <div className="flex items-center gap-2 mb-1">
            {/* One Save Changes, in place of Go Live, commits every staged edit together — Budget tab
                (budget / strategy / cap / target CPL), interests, and placements (#6076). */}
            {hasUnsavedChanges && (
              <SaveChangesButton
                onClick={saveAllChanges}
                saving={anySaving}
                className="h-9 shadow-lg animate-bounce-in-right"
              />
            )}
            {showLaunchActions && !hasUnsavedChanges && <launchControl.PaymentGate />}
            {showLaunchActions && !hasUnsavedChanges && data && data.status !== 'ACTIVE' && (
              // Green is the "push to an external platform" special case — same as Google's Go Live.
              // #6163 P1-7: the readiness reasons used to live in the disabled button's title, which
              // disabled:pointer-events-none makes permanently unreachable (the banned silently-disabled
              // pattern). Surface the unmet checks visibly beside the button instead.
              <div className="flex flex-col items-start gap-1">
                <Button
                  className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  onClick={launchControl.launch}
                  disabled={!launchControl.canLaunch}
                >
                  {launchControl.isLaunching ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Going Live...
                    </>
                  ) : (
                    <>
                      Go Live
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
                {launchControl.unmetChecks.length > 0 && (
                  // max-w keeps the checklist wrapping under the button instead of stretching the
                  // toolbar row into the tab strip (#6215 test round).
                  <p className="max-w-xs text-[11px] leading-snug text-amber-700">
                    Before going live: {launchControl.unmetChecks.join(' · ')}
                  </p>
                )}
              </div>
            )}
            {/* Pause Campaign + Delete Campaign live in the Campaign dropdown — Google's header
                placement (#6189); Go Live doubles as the resume path, so pausing while ACTIVE
                is the dropdown's only state action. Delete = soft delete (#6179); see canDelete
                for when the dropdown is withheld. */}
            {canDelete && (
              <MetaCampaignActionsDropdown
                canPause={showLaunchActions && data?.status === 'ACTIVE'}
                onPause={pause}
                isPausing={isPausing}
                onDelete={() => setShowDeleteConfirm(true)}
                isDeleting={deleteCampaign.isPending}
              />
            )}
          </div>
        )}
      </div>
      {isGenerating && progress.data ? (
        // While generating, the progress banner + ACTIVITY feed render INSIDE the tab-attached
        // white card — exactly Google's structure (its banner/feed nest in the Overview card
        // body), so the two platforms' generating states are visually identical (#5981).
        <Card className="rounded-lg rounded-tl-none border bg-white">
          <CardContent className="p-6">
            <MetaGeneratingCard progress={progress.data} />
            {/* Renders nothing until the first event lands; leaves with the generating card when
                the task ends (the backend stream expires an hour after the last event). */}
            <MetaCampaignActivityFeed slug={slug} isGenerating className="mt-4" />
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-lg rounded-tl-none border bg-white">
          <CardContent className="p-6">
            {isLoading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                <Spinner className="w-4 h-4" /> Loading Meta campaign…
              </div>
            ) : isError || !data ? (
              <div className="flex items-start gap-3 rounded-lg border-l-4 border-[#FCA5A5] bg-[#FEE2E2] p-4 text-[#B91C1C]">
                <AlertCircle className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Could not load this Meta campaign.</p>
                  <p className="text-xs">Try refreshing. If it keeps failing, check the Meta integration is still connected in Settings.</p>
                </div>
              </div>
            ) : activeTab === 'overview' ? (
              <MetaCampaignOverview campaign={data} />
            ) : activeTab === 'assets' ? (
              <MetaCampaignAssets
                campaign={data}
                currencySymbol={currencySymbol}
                activeSubTab={subSegment}
                onSubTabChange={navigateToAssetsSubTab}
              />
            ) : activeTab === 'targeting' ? (
              <MetaTargetingTab campaign={data} />
            ) : activeTab === 'budget' ? (
              <MetaBudgetTab campaign={data} currencySymbol={currencySymbol} />
            ) : (
              <MetaConversionsTab campaign={data} />
            )}
          </CardContent>
        </Card>
      )}
      {UnsavedChangesModal}
      <ConformationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Campaign"
        description={`Are you sure you want to delete "${data?.name ?? 'this campaign'}"? It stops delivering and leaves the campaign list; its spend history stays in reports.`}
        confirmText="Delete Campaign"
        cancelText="Cancel"
        isLoading={deleteCampaign.isPending}
      />
    </div>
  );
}

/**
 * Per-campaign progress card shown while the Celery generation task is fanning out (#4288 Phase 4).
 *
 * Visual sibling of Google's progress banner in ``routes/.../$campaignslug/_slug/index.tsx``: cobalt-
 * purple alert, white-rounded icon plate with a spinning ``Loader2``, an overall progress bar, and a
 * row per backend resource (ad sets, ads) showing ``completed/total``. Meta's progress feed doesn't
 * surface "in flight" counts per resource the way Google's does, so the per-row suffix is just the
 * count — no ``(+N in progress)`` chip.
 */
function MetaGeneratingCard({ progress }: { progress: MetaCampaignGenerationProgress }) {
  const sumTotal = progress.ads.total + progress.ad_sets.total;
  const sumCompleted = progress.ads.completed + progress.ad_sets.completed;
  const percent = sumTotal > 0 ? Math.round((sumCompleted / sumTotal) * 100) : 0;

  const rows: { label: string; completed: number; total: number }[] = [
    { label: 'Ad Sets', completed: progress.ad_sets.completed, total: progress.ad_sets.total },
    { label: 'Ads', completed: progress.ads.completed, total: progress.ads.total },
  ];

  return (
    <div className="bg-[#EDE9FE] border-l-4 border-l-[#290BD9] rounded-lg p-6">
      <div className="flex items-start gap-4">
        <div className="bg-white rounded-lg shadow p-3">
          <Loader2 className="h-6 w-6 text-[#290BD9] animate-spin" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Campaign</h3>
          {/* Copy matches Google's generating banner (#5981 — no text drift); only the honest
              duration differs (Meta generation runs a couple of minutes, not 5-10). */}
          <p className="text-gray-900 mb-3">
            Your campaign is currently being generated. This can take a couple of minutes.
          </p>
          <div className="mt-4">
            <div className="w-3/5 bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#290BD9] h-2 rounded-full transition-all duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="text-sm text-gray-700 mt-1">{percent}% complete</p>
            <div className="mt-3 space-y-2">
              {rows.map((row) => (
                <div key={row.label} className="flex items-center justify-between">
                  <span className="text-gray-900">{row.label}</span>
                  <span className="text-gray-900">
                    {row.completed}/{row.total}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Read-only Meta campaign overview — visual sibling of Google's :file:`campaign-info-view.tsx`
 * via the shared :class:`CampaignInfoForm` (#4288 Phase 3b). Editing isn't wired here; Phase 6+
 * scaling actions will mutate. The field list is built by :func:`metaCampaignInfoFields` in the
 * adapter so the form stays platform-thin.
 */
function MetaCampaignOverview({ campaign }: { campaign: MetaCampaignDetailResponse }) {
  // No metrics block here — Google's Overview is the pure info form, and the campaign's numbers
  // live on the Meta Performance page (#5981 — no layout drift).
  return <CampaignInfoForm fields={metaCampaignInfoFields(campaign)} />;
}

/** One labelled metric cell in the campaign metrics strip. */

/** Compact one-line metrics summary for an ad set / ad row (Phase 7). Renders nothing without data. */
function MetricsLine({
  row,
  currencySymbol,
}: {
  row: MetaAdSetPerformanceRow | MetaCreativePerformanceRow | undefined;
  currencySymbol: string;
}) {
  if (!row) return null;
  return (
    <p className="mt-1 text-[11px] text-gray-500">
      {`Spend ${formatCents(row.spend_cents, currencySymbol)} · ${row.leads} leads · CPL ${formatCents(row.cpl_cents, currencySymbol)} · CTR ${formatCtr(row.ctr)} · Freq ${formatFrequency(row.frequency)}`}
    </p>
  );
}






/**
 * The signed-URL creative thumbnail, letterboxed like /assets. Shared by the campaign Assets → Visuals
 * grid and the ad detail card (#4277). Fetches the asset the same way /assets does; a load error falls
 * back to a placeholder rather than a broken-image icon. `enabled` is gated on the FK inside the query.
 */
export function CreativeThumbnail({ assetId, alt, className }: { assetId: number; alt: string; className?: string }) {
  const { data: asset, isLoading } = useQuery(creativeAssetQueryOptions(assetId));
  const [imgError, setImgError] = useState(false);
  return (
    <div className={`flex h-40 items-center justify-center overflow-hidden bg-slate-100 ${className ?? ''}`}>
      {isLoading ? (
        <Spinner className="h-5 w-5" />
      ) : asset?.signed_url && !imgError ? (
        <img
          src={asset.signed_url}
          alt={alt}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex flex-col items-center gap-1 text-gray-400">
          <ImageOff className="h-6 w-6" />
          <span className="text-[11px]">Image unavailable</span>
        </div>
      )}
    </div>
  );
}

/** One tile in the campaign Assets → Visuals grid: the creative plus the ad name(s) that use it. */
function MetaCampaignVisualTile({ assetId, adNames }: { assetId: number; adNames: string[] }) {
  return (
    <div className="overflow-hidden rounded-md border bg-white">
      <CreativeThumbnail assetId={assetId} alt={adNames[0] ?? `Asset ${assetId}`} />
      <p className="truncate p-2 text-[11px] text-gray-500" title={adNames.join(', ')}>
        {adNames.length === 1 ? adNames[0] : `${adNames.length} ads`}
      </p>
    </div>
  );
}

/**
 * The Assets tab (#5981) — Google's underlined sub-tab chrome over Meta's real asset structure.
 *
 * Two sub-tabs: Ad Sets (the DataTable) and Visuals (the unique creatives used across the campaign's
 * ads, #4277 — images today, video later). Google's Assets carries search-ad extension types (sitelinks,
 * callouts, …) with no Meta analog. Individual ads are NOT shown at the campaign level (their editable
 * copy lives on the ad detail page, /campaigns/meta/{slug}/ad/{id}); the Visuals grid is read-only and
 * links each creative to the ad(s) that use it. MetaAdCard (below) is the inline-editable ad used there.
 */
function MetaCampaignAssets({
  campaign,
  currencySymbol,
  activeSubTab,
  onSubTabChange,
}: {
  campaign: MetaCampaignDetailResponse;
  currencySymbol: string;
  activeSubTab: string | undefined;
  onSubTabChange: (subId: string, replace?: boolean) => void;
}) {
  // The active sub-tab lives in the URL hash (`#assets/ad-sets`) so it's shareable and survives a
  // refresh (#6169). Default to Ad Sets, reflecting that default into the URL on first landing.
  const validSubTab = activeSubTab === 'visuals' ? 'visuals' : 'ad-sets';
  useEffect(() => {
    if (activeSubTab !== 'ad-sets' && activeSubTab !== 'visuals') onSubTabChange('ad-sets', true);
  }, [activeSubTab, onSubTabChange]);
  // Snapshot-backed per-ad-set metrics for the selected account (Phase 7), keyed by Meta-side id.
  const { data: adSetPerf } = useQuery(metaAdSetPerformanceQueryOptions());
  const adSetMetrics = new Map<string, MetaAdSetPerformanceRow>(
    (adSetPerf?.rows ?? []).map((row) => [row.meta_ad_set_id, row]),
  );
  // Add Ad Set (#6211): only a PUSHED campaign can grow — the create task needs the Meta-side ids.
  const [showAddAdSet, setShowAddAdSet] = useState(false);
  const canAddAdSet = campaign.meta_campaign_id != null;

  if (campaign.ad_sets.length === 0) {
    return <p className="py-2 text-sm text-gray-500">No ad sets yet.</p>;
  }

  const subTabs = [
    { id: 'ad-sets' as const, label: 'Ad Sets' },
    { id: 'visuals' as const, label: 'Visuals' },
  ];

  const adSetColumns: ColumnDef<MetaAdSetResponse, unknown>[] = [
    { accessorKey: 'name', header: 'Name' },
    {
      id: 'budget',
      header: 'Daily Budget',
      cell: ({ row }) =>
        row.original.daily_budget_cents != null
          ? `${formatCents(row.original.daily_budget_cents, currencySymbol)}/day`
          : 'Campaign budget',
    },
    {
      accessorKey: 'optimization_goal',
      header: 'Optimization Goal',
      cell: ({ row }) => humanizeMetaEnum(row.original.optimization_goal),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => humanizeMetaEnum(row.original.status),
    },
    {
      id: 'spend',
      header: 'Spend',
      cell: ({ row }) => {
        const metric = adSetMetrics.get(row.original.meta_ad_set_id);
        return metric ? formatCents(metric.spend_cents, currencySymbol) : '—';
      },
    },
    {
      id: 'leads',
      header: 'Leads',
      cell: ({ row }) => adSetMetrics.get(row.original.meta_ad_set_id)?.leads ?? '—',
    },
  ];

  // Unique creatives across the campaign's ads, each mapped to the ad name(s) using it. One creative
  // per ad today (images), but dedup keeps the grid honest and video slots in the same way later.
  const visualsByAsset = new Map<number, string[]>();
  for (const adSet of campaign.ad_sets) {
    for (const ad of adSet.ads) {
      if (!ad.creative_asset_id) continue;
      visualsByAsset.set(ad.creative_asset_id, [...(visualsByAsset.get(ad.creative_asset_id) ?? []), ad.name]);
    }
  }
  const visuals = [...visualsByAsset.entries()];

  return (
    <div className="space-y-6">
      {/* Underlined sub-tabs — the exact chrome Google's Assets tab uses (#5981, no visual drift). */}
      <div className="-mt-2.5 border-b border-gray-200 flex items-end justify-between">
        <div className="flex gap-0">
          {subTabs.map((tab) => {
            const isActive = validSubTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onSubTabChange(tab.id)}
                className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                  isActive
                    ? 'border-[#290BD9] text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {validSubTab === 'ad-sets' && canAddAdSet && (
          <Button variant="outline" className="mb-1.5 h-9 text-gray-900" onClick={() => setShowAddAdSet(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Add Ad Set
          </Button>
        )}
      </div>
      {showAddAdSet && (
        <AddMetaAdSetModal campaign={campaign} currencySymbol={currencySymbol} onClose={() => setShowAddAdSet(false)} />
      )}

      {validSubTab === 'ad-sets' ? (
        <DataTable
          data={campaign.ad_sets}
          columns={adSetColumns}
          variant="flush"
          pageSize={10}
          getRowId={(row) => String(row.id)}
        />
      ) : visuals.length === 0 ? (
        <p className="py-2 text-sm text-gray-500">No visuals yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-4 xl:grid-cols-4">
          {visuals.map(([assetId, adNames]) => (
            <MetaCampaignVisualTile key={assetId} assetId={assetId} adNames={adNames} />
          ))}
        </div>
      )}
    </div>
  );
}

/** The three editable copy fields, as one draft object. */
type MetaAdCopyDraft = { headline: string; primary_text: string; description: string };
const COPY_FIELDS = ['headline', 'primary_text', 'description'] as const;

// Mirrors the backend `MetaAdCopyUpdateRequest` (headline/description max_length=255; primary_text
// unbounded on both sides), so the field stops input where the server would 422.
const COPY_FIELD_MAX_LENGTH = 255;

// How long the blur-time 'Required' flash stays in the counter slot before clearing.
const BLUR_ERROR_FLASH_MS = 5000;
const COPY_FIELD_LABELS: Record<(typeof COPY_FIELDS)[number], string> = {
  headline: 'Headline',
  primary_text: 'Primary Text',
  description: 'Description',
};

function copyFromAd(ad: MetaAdResponse): MetaAdCopyDraft {
  return { headline: ad.headline ?? '', primary_text: ad.primary_text, description: ad.description ?? '' };
}

/**
 * The copy-editing state for one Meta ad, lifted out of MetaAdCard (#6177) so the ad detail page can
 * host Save Changes / Cancel in its top toolbar (the standard placement) rather than inline.
 *
 * Edits are held as a local draft and pushed only on an explicit Save: every persisted change mints
 * a NEW Meta creative server-side (creatives are immutable) and can re-enter Meta review, so the
 * save-per-debounce behaviour other ``EditableField`` callers use would spray creatives per pause in
 * typing. Regenerate replaces all three fields with a fresh LLM pass against the campaign's offer URL.
 * Tolerates an undefined ad (the ad detail page calls this before its query resolves).
 */
export interface MetaAdCopyEditor {
  draft: MetaAdCopyDraft;
  fieldErrors: Partial<Record<(typeof COPY_FIELDS)[number], string>>;
  isDirty: boolean;
  isBusy: boolean;
  isSaving: boolean;
  isRegenerating: boolean;
  setField: (field: (typeof COPY_FIELDS)[number], value: string) => void;
  handleFieldBlur: (field: (typeof COPY_FIELDS)[number], value: string) => void;
  /** Resolves true when the copy persisted; false on validation failure (fields marked) or a
   *  server error (toasted). Callers that gate navigation on the save must check it. */
  handleSave: () => Promise<boolean>;
  handleRegenerate: () => Promise<void>;
  resetDraft: () => void;
}

export function useMetaAdCopyEditor(ad: MetaAdResponse | undefined, campaignSlug: string): MetaAdCopyEditor {
  const { toast } = useToast();
  const updateCopy = useUpdateMetaAdCopyMutation(campaignSlug);
  const regenerate = useRegenerateMetaAdCopyMutation(campaignSlug);

  const serverCopy: MetaAdCopyDraft = ad
    ? copyFromAd(ad)
    : { headline: '', primary_text: '', description: '' };
  const [draft, setDraft] = useState<MetaAdCopyDraft>(serverCopy);
  // Blur-time validation, Google-style (the headline-management cells): blurring a blanked field
  // restores the saved copy and shows a transient 'Required' where the counter sits. Save-time
  // marking stays as the backstop.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<(typeof COPY_FIELDS)[number], string>>>({});
  const errorTimersRef = useRef<Partial<Record<(typeof COPY_FIELDS)[number], ReturnType<typeof setTimeout>>>>({});
  useEffect(() => {
    const timers = errorTimersRef.current;
    return () => Object.values(timers).forEach((timer) => clearTimeout(timer));
  }, []);

  // Re-sync the draft when the server copy changes (after a save / regenerate refetch) or when the ad
  // itself changes — keying on ad.id covers navigating between two ads with identical copy (#6163 P0-5).
  useEffect(() => {
    if (!ad) return;
    setDraft(copyFromAd(ad));
    setFieldErrors({});
  }, [ad?.id, ad?.headline, ad?.primary_text, ad?.description]);

  const isDirty = !!ad && COPY_FIELDS.some((field) => draft[field] !== serverCopy[field]);
  const isBusy = updateCopy.isPending || regenerate.isPending;

  const setField = (field: (typeof COPY_FIELDS)[number], value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Google's blank-blur behaviour (headline-management cells): snap the field back to the saved
  // copy and flash 'Required' in the counter slot for 5s — the user never sits in a blanked state.
  const handleFieldBlur = (field: (typeof COPY_FIELDS)[number], value: string) => {
    if (!ad || value.trim() !== '' || serverCopy[field].trim() === '') return;
    setDraft((prev) => ({ ...prev, [field]: serverCopy[field] }));
    setFieldErrors((prev) => ({ ...prev, [field]: 'Required' }));
    clearTimeout(errorTimersRef.current[field]);
    errorTimersRef.current[field] = setTimeout(
      () => setFieldErrors((prev) => ({ ...prev, [field]: undefined })),
      BLUR_ERROR_FLASH_MS,
    );
  };

  const handleSave = async (): Promise<boolean> => {
    if (!ad) return false;
    const errors: typeof fieldErrors = {};
    for (const field of COPY_FIELDS) {
      if (draft[field] !== serverCopy[field] && !draft[field].trim()) errors[field] = 'Required';
    }
    if (Object.values(errors).some(Boolean)) {
      setFieldErrors(errors);
      return false;
    }
    // Only the changed fields go in the PATCH — the backend keeps omitted fields verbatim.
    const body: MetaAdCopyUpdateRequest = {};
    for (const field of COPY_FIELDS) {
      if (draft[field] !== serverCopy[field]) body[field] = draft[field].trim();
    }
    try {
      await updateCopy.mutateAsync({ adId: ad.id, body });
      return true;
    } catch (error) {
      toast({
        title: 'Could Not Update Ad Copy',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleRegenerate = async () => {
    if (!ad) return;
    try {
      await regenerate.mutateAsync(ad.id);
    } catch (error) {
      toast({
        title: 'Could Not Regenerate Ad Copy',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const resetDraft = () => {
    if (!ad) return;
    setDraft(copyFromAd(ad));
    setFieldErrors({});
  };

  return {
    draft,
    fieldErrors,
    isDirty,
    isBusy,
    isSaving: updateCopy.isPending,
    isRegenerating: regenerate.isPending,
    setField,
    handleFieldBlur,
    handleSave,
    handleRegenerate,
    resetDraft,
  };
}

/**
 * One ad's presentational copy card (#5981): the three editable fields + Regenerate. Save/Cancel live
 * in the ad detail page's top toolbar, driven by the shared editor from useMetaAdCopyEditor (#6177).
 */
export function MetaAdCard({
  ad,
  metric,
  currencySymbol,
  hideVisual = false,
  editor,
}: {
  ad: MetaAdResponse;
  metric: MetaCreativePerformanceRow | undefined;
  currencySymbol: string;
  /** Hide the internal creative thumbnail when the visual is rendered in a separate column (#6177). */
  hideVisual?: boolean;
  editor: MetaAdCopyEditor;
}) {
  return (
    <div className="space-y-4">
      {/* Creative preview — the same letterboxed thumbnail the campaign Visuals grid and /assets use.
          Hidden on the ad detail page, where the visual lives in its own column (#6177). */}
      {!hideVisual && (
        <CreativeThumbnail assetId={ad.creative_asset_id} alt={ad.name} className="rounded-md border" />
      )}
      <div className="space-y-4">
        {COPY_FIELDS.map((field) => (
          <div key={field}>
            <p className="mb-1.5 text-sm font-medium text-gray-700">{COPY_FIELD_LABELS[field]}</p>
            <EditableField
              value={editor.draft[field]}
              onChange={(value) => editor.setField(field, value)}
              onBlur={(value) => editor.handleFieldBlur(field, value)}
              errorMessage={editor.fieldErrors[field]}
              maxLength={field === 'primary_text' ? undefined : COPY_FIELD_MAX_LENGTH}
              placeholder={COPY_FIELD_LABELS[field]}
              disabled={editor.isBusy}
              hideBullet
              alwaysBordered
            />
          </div>
        ))}
      </div>
      <MetricsLine row={metric} currencySymbol={currencySymbol} />
      {/* Save Changes / Cancel live in the ad detail page's top toolbar (#6177); only Regenerate here. */}
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-9 text-gray-900"
          onClick={editor.handleRegenerate}
          disabled={editor.isBusy}
        >
          {editor.isRegenerating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          Regenerate Copy
        </Button>
      </div>
    </div>
  );
}
